/* ==========================================================================
   Complete Onboarding Modal
   Task F36: Confirm sending admin invitation email
   ========================================================================== */

import { useState } from 'react'
import { Modal } from '@/components/ui/Modal'
import { Button } from '@/components/ui/Button'
import { Alert } from '@/components/ui/Alert'
import { markComplete, type IntakeDetail } from '@/api/internal'
import { ApiClientError } from '@/api/client'

interface CompleteModalProps {
  open: boolean
  onClose: () => void
  intake: IntakeDetail
  onSuccess: () => void
}

export function CompleteModal({
  open,
  onClose,
  intake,
  onSuccess,
}: CompleteModalProps) {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string>()

  const handleComplete = async () => {
    setLoading(true)
    setError(undefined)

    try {
      await markComplete(intake.id)
      onSuccess()
      onClose()
    } catch (err) {
      if (err instanceof ApiClientError) {
        setError(err.getUserMessage())
      } else {
        setError('An unexpected error occurred')
      }
    } finally {
      setLoading(false)
    }
  }

  const portalUrl = intake.pharmacy_slug
    ? `/rx/${intake.pharmacy_slug}`
    : `/rx/${intake.preferred_slug}`

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Complete & Invite Admin"
      size="md"
    >
      {error && (
        <Alert variant="error" className="mb-4">
          {error}
        </Alert>
      )}

      <p className="text-sm text-charcoal mb-4">
        This will send the pharmacy admin their first login invitation.
      </p>

      <div className="space-y-3 mb-4">
        <div className="flex justify-between text-sm">
          <span className="text-charcoal/60">Pharmacy</span>
          <span className="font-medium text-charcoal">{intake.pharmacy_name}</span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-charcoal/60">Admin Email</span>
          <span className="text-charcoal">{intake.admin_email}</span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-charcoal/60">Portal URL</span>
          <span className="text-charcoal font-mono text-xs">{portalUrl}</span>
        </div>
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded-md p-3 mb-6">
        <p className="text-sm text-amber-800">
          The pharmacy admin will receive an email with a link to set up their password.
        </p>
      </div>

      <div className="flex gap-3">
        <Button
          variant="secondary"
          onClick={onClose}
          disabled={loading}
          className="flex-1"
        >
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleComplete}
          loading={loading}
          disabled={loading}
          className="flex-1"
        >
          Complete & Send Invite
        </Button>
      </div>
    </Modal>
  )
}

export default CompleteModal
